'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { EVENTO_ERRO_EXIBIDO } from '@/shared/components/providers/ToastProvider';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { SEGUNDOS_ATE_FECHAR_APOS_ENVIO, SEGUNDOS_ATE_RECOLHER } from '../constants/catalogoMomentos';
import { carregarContextoPesquisa, exibirPesquisa, fecharPesquisa } from '../store/actions';
import { selectComentario, selectContextoCarregado, selectContextoPesquisa, selectCorrelacaoId, selectEnviandoComentario, selectEstadoPesquisa, selectMomentoAtivo, selectNotaSelecionada, selectOrigemAtiva, selectTimerPausado } from '../store/selectors';
import { comentarioAlterado, pesquisaExibida, pesquisaFechada, sessaoRestaurada, timerPausado, timerRetomado } from '../store/slice';
import { montarOrigem } from '../utils/montarOrigem';
import { resolverPesquisa } from '../utils/resolverPesquisa';
import { lerMarcaDeSessao, marcarPesquisaNaSessao } from '../utils/sessaoDaPesquisa';

/**
 * Orquestração do cartão. Só o `PesquisaSatisfacaoToast` deve chamar: dois
 * consumidores dariam dois timers e duas resoluções concorrentes para o mesmo
 * estado.
 */
export function usePesquisaSatisfacao() {
  const $ = _c(73);
  const dispatch = useAppDispatch();
  const rotaResolvida = usePathname();
  const momento = useAppSelector(selectMomentoAtivo);
  const estado = useAppSelector(selectEstadoPesquisa);
  const nota = useAppSelector(selectNotaSelecionada);
  const comentario = useAppSelector(selectComentario);
  const correlacaoId = useAppSelector(selectCorrelacaoId);
  const origemAtiva = useAppSelector(selectOrigemAtiva);
  const pausado = useAppSelector(selectTimerPausado);
  const enviandoComentario = useAppSelector(selectEnviandoComentario);
  const contextoCarregado = useAppSelector(selectContextoCarregado);
  const contexto = useAppSelector(selectContextoPesquisa);
  const usuarioId = useAppSelector(_temp);
  const [segundosRestantes, setSegundosRestantes] = useState(SEGUNDOS_ATE_RECOLHER);
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== usuarioId) {
    t0 = () => {
      if (!usuarioId) {
        return;
      }
      dispatch(sessaoRestaurada(lerMarcaDeSessao()));
      dispatch(carregarContextoPesquisa());
    };
    t1 = [usuarioId, dispatch];
    $[0] = dispatch;
    $[1] = usuarioId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== contexto || $[5] !== contextoCarregado || $[6] !== dispatch || $[7] !== momento || $[8] !== rotaResolvida) {
    t2 = () => {
      if (momento) {
        return;
      }
      if (!contextoCarregado) {
        return;
      }
      const agoraIso = new Date().toISOString();
      const proxima = resolverPesquisa({
        agoraIso,
        ...contexto
      });
      if (!proxima) {
        return;
      }
      const evento = contexto.eventos.find(item => item.momentoId === proxima.id);
      const correlacao = crypto.randomUUID();
      const origem = montarOrigem(proxima, rotaResolvida, evento?.recurso ?? null);
      marcarPesquisaNaSessao();
      dispatch(pesquisaExibida({
        momentoId: proxima.id,
        exibidaEmIso: agoraIso,
        correlacaoId: correlacao,
        origem
      }));
      dispatch(exibirPesquisa({
        momentoId: proxima.id,
        correlacaoId: correlacao,
        origem
      }));
    };
    t3 = [momento, contexto, contextoCarregado, rotaResolvida, dispatch];
    $[4] = contexto;
    $[5] = contextoCarregado;
    $[6] = dispatch;
    $[7] = momento;
    $[8] = rotaResolvida;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSegundosRestantes(SEGUNDOS_ATE_RECOLHER);
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = momento?.id;
  let t6;
  if ($[12] !== t5) {
    t6 = [t5];
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t4, t6);
  let t7;
  if ($[14] !== correlacaoId || $[15] !== dispatch || $[16] !== origemAtiva) {
    t7 = (desfecho, valorDaNota) => {
      if (!correlacaoId || !origemAtiva) {
        return;
      }
      dispatch(fecharPesquisa({
        correlacaoId,
        desfecho,
        nota: valorDaNota,
        comentario: null,
        origem: origemAtiva
      }));
    };
    $[14] = correlacaoId;
    $[15] = dispatch;
    $[16] = origemAtiva;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const encerrar = t7;
  let t8;
  let t9;
  if ($[18] !== dispatch || $[19] !== encerrar || $[20] !== estado || $[21] !== momento || $[22] !== pausado || $[23] !== segundosRestantes) {
    t8 = () => {
      if (!momento || estado !== "PERGUNTA" || pausado) {
        return;
      }
      if (segundosRestantes <= 0) {
        encerrar("EXPIRADA", null);
        dispatch(pesquisaFechada());
        return;
      }
      const temporizador = setTimeout(() => setSegundosRestantes(_temp2), 1000);
      return () => clearTimeout(temporizador);
    };
    t9 = [momento, estado, pausado, segundosRestantes, encerrar, dispatch];
    $[18] = dispatch;
    $[19] = encerrar;
    $[20] = estado;
    $[21] = momento;
    $[22] = pausado;
    $[23] = segundosRestantes;
    $[24] = t8;
    $[25] = t9;
  } else {
    t8 = $[24];
    t9 = $[25];
  }
  useEffect(t8, t9);
  let t10;
  if ($[26] !== dispatch) {
    t10 = () => {
      const sincronizarComAVisibilidade = () => dispatch(document.hidden ? timerPausado() : timerRetomado());
      sincronizarComAVisibilidade();
      document.addEventListener("visibilitychange", sincronizarComAVisibilidade);
      return () => document.removeEventListener("visibilitychange", sincronizarComAVisibilidade);
    };
    $[26] = dispatch;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const t11 = momento?.id;
  let t12;
  if ($[28] !== dispatch || $[29] !== t11) {
    t12 = [t11, dispatch];
    $[28] = dispatch;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  useEffect(t10, t12);
  let t13;
  let t14;
  if ($[31] !== dispatch || $[32] !== encerrar || $[33] !== estado || $[34] !== momento) {
    t13 = () => {
      if (!momento || estado !== "PERGUNTA") {
        return;
      }
      const aoAparecerErro = () => {
        encerrar("DISPENSADA", null);
        dispatch(pesquisaFechada());
      };
      window.addEventListener(EVENTO_ERRO_EXIBIDO, aoAparecerErro);
      return () => window.removeEventListener(EVENTO_ERRO_EXIBIDO, aoAparecerErro);
    };
    t14 = [momento, estado, encerrar, dispatch];
    $[31] = dispatch;
    $[32] = encerrar;
    $[33] = estado;
    $[34] = momento;
    $[35] = t13;
    $[36] = t14;
  } else {
    t13 = $[35];
    t14 = $[36];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[37] !== dispatch || $[38] !== estado) {
    t15 = () => {
      if (estado !== "ENVIADO") {
        return;
      }
      const temporizador_0 = setTimeout(() => dispatch(pesquisaFechada()), SEGUNDOS_ATE_FECHAR_APOS_ENVIO * 1000);
      return () => clearTimeout(temporizador_0);
    };
    t16 = [estado, dispatch];
    $[37] = dispatch;
    $[38] = estado;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
  const progressoPercentual = Math.max(0, segundosRestantes / SEGUNDOS_ATE_RECOLHER * 100);
  let t17;
  if ($[41] !== encerrar || $[42] !== momento) {
    t17 = valor => {
      if (!momento) {
        return;
      }
      encerrar("RESPONDIDA", valor);
    };
    $[41] = encerrar;
    $[42] = momento;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const selecionarNota = t17;
  let t18;
  if ($[44] !== dispatch) {
    t18 = valor_0 => dispatch(comentarioAlterado(valor_0));
    $[44] = dispatch;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const alterarComentario = t18;
  let t19;
  if ($[46] !== comentario || $[47] !== correlacaoId || $[48] !== dispatch || $[49] !== nota || $[50] !== origemAtiva) {
    t19 = () => {
      if (!correlacaoId || !origemAtiva || nota === null) {
        return;
      }
      dispatch(fecharPesquisa({
        correlacaoId,
        desfecho: "RESPONDIDA",
        nota,
        comentario,
        origem: origemAtiva
      }));
    };
    $[46] = comentario;
    $[47] = correlacaoId;
    $[48] = dispatch;
    $[49] = nota;
    $[50] = origemAtiva;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const submeterComentario = t19;
  let t20;
  if ($[52] !== dispatch || $[53] !== encerrar || $[54] !== estado) {
    t20 = () => {
      if (estado === "PERGUNTA") {
        encerrar("DISPENSADA", null);
      }
      dispatch(pesquisaFechada());
    };
    $[52] = dispatch;
    $[53] = encerrar;
    $[54] = estado;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const fechar = t20;
  let t21;
  if ($[56] !== dispatch) {
    t21 = () => dispatch(timerPausado());
    $[56] = dispatch;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  const pausar = t21;
  let t22;
  if ($[58] !== dispatch) {
    t22 = () => dispatch(timerRetomado());
    $[58] = dispatch;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  const retomar = t22;
  let t23;
  if ($[60] !== alterarComentario || $[61] !== comentario || $[62] !== enviandoComentario || $[63] !== estado || $[64] !== fechar || $[65] !== momento || $[66] !== nota || $[67] !== pausar || $[68] !== progressoPercentual || $[69] !== retomar || $[70] !== selecionarNota || $[71] !== submeterComentario) {
    t23 = {
      momento,
      estado,
      nota,
      comentario,
      enviandoComentario,
      progressoPercentual,
      selecionarNota,
      alterarComentario,
      submeterComentario,
      fechar,
      pausar,
      retomar
    };
    $[60] = alterarComentario;
    $[61] = comentario;
    $[62] = enviandoComentario;
    $[63] = estado;
    $[64] = fechar;
    $[65] = momento;
    $[66] = nota;
    $[67] = pausar;
    $[68] = progressoPercentual;
    $[69] = retomar;
    $[70] = selecionarNota;
    $[71] = submeterComentario;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  return t23;
}
function _temp2(atual) {
  return atual - 1;
}
function _temp(state) {
  return state.auth.user?.id ?? null;
}